import {
  Anchor,
  Badge,
  Code,
  Group,
  NumberFormatter,
  Skeleton,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core'

import { useLiquidityProviders } from '@repo/common/queries/admin/redeem/redeem_queries.ts'
import type {
  LiquidityPool,
  LiquidityPoolTranche,
  LiquidityProviderBucket,
  LiquidityProviderBucketAssetBalance,
  LiquidityProviderWithBuckets,
  LiquidityTrancheBucket,
} from '@repo/common/queries/admin/redeem/types.ts'

import { TowerCard } from '@/components/ui/TowerCard'

import classes from './LiquidityOverview.module.css'

type LiquidityPoolWithRedeemable = LiquidityPool & {
  redeemable: { id: string; name: string }
}

const TRANCHE_COLORS = [
  'var(--mantine-color-blue-7)',
  'var(--mantine-color-teal-7)',
  'var(--mantine-color-violet-7)',
  'var(--mantine-color-orange-7)',
  'var(--mantine-color-cyan-7)',
  'var(--mantine-color-pink-7)',
]

function getFillColor(ratio: number): string {
  if (ratio < 0.6) return 'var(--mantine-color-green-6)'
  if (ratio < 0.85) return 'var(--mantine-color-yellow-6)'
  return 'var(--mantine-color-red-6)'
}

function formatAmount(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return value.toFixed(2)
}

function formatAssetLabel(ab: LiquidityProviderBucketAssetBalance): string {
  if (ab.crypto_asset) {
    return `${ab.crypto_asset.symbol} ${ab.crypto_asset.network_name}`
  }
  return ab.asset_id
}

const PIE_COLORS = [
  'var(--mantine-color-blue-6)',
  'var(--mantine-color-teal-6)',
  'var(--mantine-color-violet-6)',
  'var(--mantine-color-orange-6)',
  'var(--mantine-color-cyan-6)',
  'var(--mantine-color-pink-6)',
]

function MiniPieChart({
  assets,
  size = 64,
}: {
  assets: LiquidityProviderBucketAssetBalance[]
  size?: number
}) {
  const total = assets.reduce((sum, a) => sum + a.balance, 0)
  if (total === 0) {
    return (
      <svg width={size} height={size} viewBox="0 0 32 32">
        <circle
          cx="16"
          cy="16"
          r="15"
          fill="none"
          stroke="var(--mantine-color-dark-4)"
          strokeWidth="2"
        />
      </svg>
    )
  }

  const r = 15
  const cx = 16
  const cy = 16
  let cumulative = 0
  const slices: React.ReactNode[] = []

  if (assets.length === 1) {
    slices.push(<circle key={0} cx={cx} cy={cy} r={r} fill={PIE_COLORS[0]} />)
  } else {
    for (let i = 0; i < assets.length; i++) {
      const fraction = (assets[i]?.balance || 0) / total || 0
      const startAngle = cumulative * 2 * Math.PI - Math.PI / 2
      cumulative += fraction
      const endAngle = cumulative * 2 * Math.PI - Math.PI / 2

      const largeArc = fraction > 0.5 ? 1 : 0
      const x1 = cx + r * Math.cos(startAngle)
      const y1 = cy + r * Math.sin(startAngle)
      const x2 = cx + r * Math.cos(endAngle)
      const y2 = cy + r * Math.sin(endAngle)

      slices.push(
        <path
          key={i}
          d={`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc},1 ${x2},${y2} Z`}
          fill={PIE_COLORS[i % PIE_COLORS.length]}
        />,
      )
    }
  }

  return (
    <svg width={size} height={size} viewBox="0 0 32 32">
      {slices}
    </svg>
  )
}

function bucketAnchorId(providerId: string, bucketId: string) {
  return `lpb-${providerId}-${bucketId}`
}

function providerAnchorId(providerId: string) {
  return `lp-${providerId}`
}

export { bucketAnchorId }
export type { LiquidityPoolWithRedeemable }

export function BucketCard({
  provider,
  bucket,
}: {
  provider: LiquidityProviderWithBuckets
  bucket: LiquidityProviderBucket
}) {
  const assets = [...bucket.balance_by_asset].sort(
    (a, b) => b.balance - a.balance,
  )

  return (
    <div
      id={bucketAnchorId(provider.id, bucket.id)}
      className={classes.bucket_card}
    >
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <Text fw={600} size="md">
            {bucket.name}
          </Text>
          <Badge variant="light" size="sm">
            {bucket.mode}
          </Badge>
        </Group>
        {bucket.fireblocks_vault_id && (
          <Code>{bucket.fireblocks_vault_id}</Code>
        )}
      </Group>

      <div className={classes.bucket_card_body}>
        <div className={classes.bucket_pie_container}>
          <MiniPieChart assets={assets} />
        </div>
        <div className={classes.bucket_assets}>
          <Text size="md" fw={600} mb={6}>
            Total:{' '}
            <NumberFormatter
              value={bucket.balance_total}
              thousandSeparator
              decimalScale={6}
            />
          </Text>
          {assets.length === 0 ? (
            <Text size="sm" c="dimmed">
              No asset balances
            </Text>
          ) : (
            <Stack gap={4}>
              {assets.map((a, i) => (
                <Group key={a.asset_id} gap="xs" wrap="nowrap">
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: PIE_COLORS[i % PIE_COLORS.length],
                      flexShrink: 0,
                    }}
                  />
                  <Text size="sm">
                    {formatAssetLabel(a)}:{' '}
                    <NumberFormatter
                      value={a.balance}
                      thousandSeparator
                      decimalScale={6}
                    />
                  </Text>
                </Group>
              ))}
            </Stack>
          )}
        </div>
      </div>
    </div>
  )
}

function TrancheTooltipContent({ tranche }: { tranche: LiquidityPoolTranche }) {
  const buckets = tranche.liquidity_tranche_buckets.nodes
  return (
    <Stack gap={4}>
      <Text size="xs" fw={600}>
        Tranche #{tranche.position}
        {tranche.size_limit == null ? ' (unlimited)' : ''}
      </Text>
      {tranche.size_limit != null && (
        <Text size="xs">
          Size limit:{' '}
          <NumberFormatter
            value={tranche.size_limit}
            thousandSeparator
            decimalScale={2}
          />
        </Text>
      )}
      <Text size="xs">
        Utilized:{' '}
        <NumberFormatter
          value={tranche.utilized_capacity}
          thousandSeparator
          decimalScale={2}
        />
      </Text>
      <Text size="xs">
        Remaining:{' '}
        <NumberFormatter
          value={tranche.remaining_capacity}
          thousandSeparator
          decimalScale={2}
        />
      </Text>
      {buckets.length > 0 && (
        <>
          <Text size="xs" fw={600} mt={4}>
            LP Buckets:
          </Text>
          {buckets.map((b: LiquidityTrancheBucket) => (
            <Text size="xs" key={b.id}>
              <Anchor
                href={`#${bucketAnchorId(b.liquidity_provider.id, b.liquidity_provider_bucket.id)}`}
                className={classes.bucket_link}
                size="xs"
              >
                {b.liquidity_provider.name} / {b.liquidity_provider_bucket.name}
              </Anchor>
              {' — '}shares: {b.shares}, fee: {b.operating_fee_percentage}%
            </Text>
          ))}
        </>
      )}
    </Stack>
  )
}

function TrancheBar({ pool }: { pool: LiquidityPoolWithRedeemable }) {
  const tranches = [...pool.liquidity_pool_tranches.nodes].sort(
    (a, b) => a.position - b.position,
  )

  const totalSize = tranches.reduce((sum, t) => {
    if (t.size_limit == null)
      return sum + t.utilized_capacity + t.remaining_capacity
    return sum + t.size_limit
  }, 0)

  if (totalSize === 0) {
    return (
      <Text size="sm" c="dimmed">
        No tranches configured
      </Text>
    )
  }

  return (
    <div className={classes.tranche_bar_container}>
      {tranches.map((tranche, idx) => {
        const isUnlimited = tranche.size_limit == null
        const segmentSize = isUnlimited
          ? tranche.utilized_capacity + tranche.remaining_capacity
          : tranche.size_limit!
        const widthPct = Math.max((segmentSize / totalSize) * 100, 5)
        const fillRatio = isUnlimited
          ? 0
          : tranche.size_limit! > 0
            ? tranche.utilized_capacity / tranche.size_limit!
            : 0

        const bgColor = TRANCHE_COLORS[idx % TRANCHE_COLORS.length]

        return (
          <Tooltip
            key={tranche.id}
            label={<TrancheTooltipContent tranche={tranche} />}
            multiline
            w={280}
            position="bottom"
          >
            <div
              className={`${classes.tranche_segment} ${isUnlimited ? classes.tranche_segment_unlimited : ''}`}
              style={{
                width: `${widthPct}%`,
                backgroundColor: bgColor,
              }}
            >
              {!isUnlimited && (
                <div
                  className={classes.tranche_fill}
                  style={{
                    width: `${fillRatio * 100}%`,
                    backgroundColor: getFillColor(fillRatio),
                  }}
                />
              )}
              <span className={classes.tranche_label}>
                {isUnlimited ? '∞' : `#${tranche.position}`}
                {' · '}
                {formatAmount(tranche.utilized_capacity)}
                {' / '}
                {isUnlimited ? '∞' : formatAmount(tranche.size_limit!)}
              </span>
            </div>
          </Tooltip>
        )
      })}
    </div>
  )
}

export function PoolCard({ pool }: { pool: LiquidityPoolWithRedeemable }) {
  const tranches = [...pool.liquidity_pool_tranches.nodes].sort(
    (a, b) => a.position - b.position,
  )

  return (
    <div className={classes.pool_card}>
      <Group justify="space-between" mb="sm">
        <Group gap="sm">
          <Text fw={600}>{pool.redeemable.name}</Text>
          <Code>{pool.id.slice(0, 8)}</Code>
        </Group>
        <Badge color={pool.active ? 'green' : 'gray'} variant="light">
          {pool.active ? 'Active' : 'Inactive'}
        </Badge>
      </Group>

      <TrancheBar pool={pool} />

      {tranches.length > 0 && (
        <Table className={classes.bucket_table} mt="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Pos</Table.Th>
              <Table.Th>Size Limit</Table.Th>
              <Table.Th>Utilized</Table.Th>
              <Table.Th>Remaining</Table.Th>
              <Table.Th>LP Buckets</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tranches.map((t) => (
              <Table.Tr key={t.id}>
                <Table.Td>{t.position}</Table.Td>
                <Table.Td>
                  {t.size_limit == null ? (
                    <Text span c="dimmed">
                      ∞
                    </Text>
                  ) : (
                    <NumberFormatter
                      value={t.size_limit}
                      thousandSeparator
                      decimalScale={2}
                    />
                  )}
                </Table.Td>
                <Table.Td>
                  <NumberFormatter
                    value={t.utilized_capacity}
                    thousandSeparator
                    decimalScale={2}
                  />
                </Table.Td>
                <Table.Td>
                  <NumberFormatter
                    value={t.remaining_capacity}
                    thousandSeparator
                    decimalScale={2}
                  />
                </Table.Td>
                <Table.Td>
                  {t.liquidity_tranche_buckets.nodes.map(
                    (b: LiquidityTrancheBucket) => (
                      <Text size="xs" key={b.id}>
                        <Anchor
                          href={`#${bucketAnchorId(b.liquidity_provider.id, b.liquidity_provider_bucket.id)}`}
                          className={classes.bucket_link}
                          size="xs"
                        >
                          {b.liquidity_provider.name} /{' '}
                          {b.liquidity_provider_bucket.name}
                        </Anchor>
                        {' ('}shares: {b.shares}, fee:{' '}
                        {b.operating_fee_percentage}%)
                      </Text>
                    ),
                  )}
                  {t.liquidity_tranche_buckets.nodes.length === 0 && (
                    <Text size="xs" c="dimmed">
                      None
                    </Text>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </div>
  )
}

export function LiquidityOverview() {
  const { data: providersData, isLoading: loadingProviders } =
    useLiquidityProviders()

  const providers = providersData?.liquidity_providers.nodes ?? []
  const totalBuckets = providers.reduce(
    (sum, p) => sum + p.liquidity_provider_buckets.nodes.length,
    0,
  )

  return (
    <Stack gap="lg" pb="50vh">
      <TowerCard title="Liquidity Providers">
        {loadingProviders ? (
          <Stack gap="sm">
            <Skeleton height={40} />
            <Skeleton height={40} />
          </Stack>
        ) : providers.length === 0 ? (
          <Text c="dimmed">No liquidity providers found</Text>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Provider</Table.Th>
                <Table.Th>Buckets</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>
                  Total Balance
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {providers.map((provider) => {
                const buckets = provider.liquidity_provider_buckets.nodes
                const providerTotal = buckets.reduce(
                  (sum, b) => sum + b.balance_total,
                  0,
                )
                return (
                  <Table.Tr key={provider.id}>
                    <Table.Td>
                      <Anchor
                        href={`#${providerAnchorId(provider.id)}`}
                        className={classes.bucket_link}
                        fw={500}
                      >
                        {provider.name}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>{buckets.length}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <NumberFormatter
                        value={providerTotal}
                        thousandSeparator
                        decimalScale={2}
                      />
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
            <Table.Tfoot>
              <Table.Tr>
                <Table.Td fw={600}>Total</Table.Td>
                <Table.Td fw={600}>{totalBuckets}</Table.Td>
                <Table.Td fw={600} style={{ textAlign: 'right' }}>
                  <NumberFormatter
                    value={providers.reduce(
                      (sum, p) =>
                        sum +
                        p.liquidity_provider_buckets.nodes.reduce(
                          (s, b) => s + b.balance_total,
                          0,
                        ),
                      0,
                    )}
                    thousandSeparator
                    decimalScale={2}
                  />
                </Table.Td>
              </Table.Tr>
            </Table.Tfoot>
          </Table>
        )}
      </TowerCard>

      {!loadingProviders &&
        providers.map((provider) => (
          <TowerCard key={provider.id} title={provider.name}>
            <div
              id={providerAnchorId(provider.id)}
              className={classes.provider_anchor}
            />
            {provider.liquidity_provider_buckets.nodes.length === 0 ? (
              <Text c="dimmed">No buckets</Text>
            ) : (
              <Stack gap="md">
                {provider.liquidity_provider_buckets.nodes.map((bucket) => (
                  <BucketCard
                    key={bucket.id}
                    provider={provider}
                    bucket={bucket}
                  />
                ))}
              </Stack>
            )}
          </TowerCard>
        ))}
    </Stack>
  )
}
